/**
 * App Loading Component
 *
 * Shows a branded loading state while the app initializes
 * (auth session restoration, config loading, health checks).
 *
 * Distinctive design: Animated logo with subtle glow, warm background gradient.
 */

import { useTranslation } from 'react-i18next';
import { useBrandName } from '@/i18n/useBrandName';

/** Health check status for display */
export type HealthCheckPhase =
  | 'checking'      // Initial health check in progress
  | 'connecting'    // Connecting to backend
  | 'starting'      // Backend is starting (loading models)
  | 'initializing'  // Auth/config initialization
  | 'ready';        // All checks passed

interface AppLoadingProps {
  /** Optional message to display below spinner */
  message?: string;
  /** Whether backend is still starting up */
  isBackendStarting?: boolean;
  /** Current phase of health check (for detailed status) */
  phase?: HealthCheckPhase;
  /** Error message if connection failed */
  error?: string | null;
}

export function AppLoading({ message, isBackendStarting, phase, error }: AppLoadingProps) {
  const { t } = useTranslation();
  // AppLoading renders while /config is in flight, so brand name typically
  // resolves to the i18n fallback (config is null) — useBrandName handles that.
  const brandName = useBrandName();

  // Determine status message based on phase
  const getStatusMessage = (): string => {
    if (message) return message;
    if (error) return error;
    switch (phase) {
      case 'checking':
        return t('health.checking', 'Checking connection...');
      case 'connecting':
        return t('health.connecting', 'Connecting to server...');
      case 'starting':
        return t('health.starting', 'Server starting...');
      case 'initializing':
        return t('health.initializing', 'Initializing app...');
      case 'ready':
        return t('health.ready', 'Ready');
      default:
        return t('common.loading', 'Loading...');
    }
  };

  // Determine indicator color based on phase
  const getIndicatorColor = (): string => {
    if (error) return 'bg-red-500';
    switch (phase) {
      case 'checking':
      case 'connecting':
        return 'bg-amber-500';
      case 'starting':
        return 'bg-citation';
      case 'initializing':
        return 'bg-primary';
      case 'ready':
        return 'bg-green-500';
      default:
        return 'bg-primary';
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Subtle animated background gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-primary/5 blur-3xl animate-pulse-subtle" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-citation/5 blur-3xl animate-pulse-subtle" style={{ animationDelay: '1s' }} />
      </div>

      <div className="text-center relative z-10">
        {/* Animated logo mark */}
        <div className="relative w-20 h-20 mx-auto mb-6">
          {/* Outer glow ring */}
          <div className="absolute inset-0 rounded-2xl bg-primary/20 animate-pulse-subtle" />
          {/* Main logo container */}
          <div className="absolute inset-1 rounded-xl bg-gradient-to-br from-primary to-primary-700 flex items-center justify-center shadow-lg animate-float">
            {/* Swiss cross motif */}
            <svg viewBox="0 0 24 24" className="w-10 h-10 text-white" fill="currentColor">
              <path d="M19 11h-6V5a1 1 0 0 0-2 0v6H5a1 1 0 0 0 0 2h6v6a1 1 0 0 0 2 0v-6h6a1 1 0 0 0 0-2z" />
            </svg>
          </div>
        </div>

        {/* App name with distinctive typography */}
        <h1 className="text-2xl font-display font-semibold text-neutral-900 mb-4 tracking-tight">
          {brandName}
        </h1>

        {/* Loading indicator bar */}
        <div className="w-32 h-1 mx-auto rounded-full bg-neutral-200 overflow-hidden">
          <div className="h-full shimmer-bg animate-shimmer rounded-full" />
        </div>

        {/* Connection status indicator */}
        <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-neutral-200">
          <div className={`w-2 h-2 rounded-full ${getIndicatorColor()} animate-pulse`} />
          <p className="text-xs font-medium text-neutral-600">
            {getStatusMessage()}
          </p>
        </div>

        {/* Backend starting indicator (additional detail) */}
        {isBackendStarting && (
          <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-citation-50 border border-citation-200">
            <div className="w-2 h-2 rounded-full bg-citation animate-pulse" />
            <p className="text-xs font-medium text-citation-700">
              {t('common.serverStarting', 'Warming up the server...')}
            </p>
          </div>
        )}

        {/* Error details if present */}
        {error && (
          <p className="mt-2 text-xs text-red-500 max-w-xs mx-auto">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
